import React from "react";

interface LessonRendererProps {
  content: string;
}

type Block =
  | { type: "heading"; level: number; text: string }
  | { type: "code"; language: string; code: string }
  | { type: "table"; headers: string[]; rows: string[][] }
  | { type: "list"; items: string[] }
  | { type: "paragraph"; text: string }
  | { type: "break" };

function parseBlocks(content: string): Block[] {
  const lines = content.split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.trimStart().startsWith("```")) {
      const lang = line.trimStart().replace("```", "").trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      blocks.push({ type: "code", language: lang, code: codeLines.join("\n") });
      i++;
      continue;
    }

    // Table (line with pipes)
    if (line.includes("|") && line.trim().startsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      // Parse: first line = headers, second = separator, rest = rows
      const parsePipeLine = (l: string) =>
        l.split("|").map((c) => c.trim()).filter((c) => c && !c.match(/^-+$/));

      if (tableLines.length >= 2) {
        const headers = parsePipeLine(tableLines[0]);
        const isSeparator = (l: string) => l.replace(/[|\-\s:]/g, "").length === 0;
        const dataStart = isSeparator(tableLines[1]) ? 2 : 1;
        const rows = tableLines.slice(dataStart).map(parsePipeLine);
        blocks.push({ type: "table", headers, rows });
      }
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#{2,4})\s+(.*)/);
    if (headingMatch) {
      blocks.push({ type: "heading", level: headingMatch[1].length, text: headingMatch[2] });
      i++;
      continue;
    }

    // List items (collect consecutive)
    if (line.trimStart().startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trimStart().startsWith("- ")) {
        items.push(lines[i].trimStart().substring(2));
        i++;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      blocks.push({ type: "break" });
      i++;
      continue;
    }

    // Paragraph
    blocks.push({ type: "paragraph", text: line });
    i++;
  }

  return blocks;
}

function renderInline(text: string): React.ReactNode[] {
  // Handle **bold** and `code` inline
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*(.+?)\*\*)|(`([^`]+?)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={match.index} className="font-semibold text-foreground">{match[2]}</strong>);
    } else if (match[4]) {
      parts.push(
        <code key={match.index} className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-primary">
          {match[4]}
        </code>
      );
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

export function LessonRenderer({ content }: LessonRendererProps) {
  const blocks = parseBlocks(content);

  return (
    <div className="space-y-4">
      {blocks.map((block, i) => {
        switch (block.type) {
          case "heading":
            if (block.level === 2)
              return (
                <h2 key={i} className="text-xl font-bold text-foreground mt-6 mb-2 border-b border-border pb-2">
                  {renderInline(block.text)}
                </h2>
              );
            if (block.level === 3)
              return (
                <h3 key={i} className="text-lg font-semibold text-foreground mt-5 mb-1.5">
                  {renderInline(block.text)}
                </h3>
              );
            return (
              <h4 key={i} className="text-base font-medium text-foreground mt-4 mb-1">
                {renderInline(block.text)}
              </h4>
            );

          case "code":
            return (
              <div key={i} className="rounded-lg overflow-hidden border border-border">
                {block.language && (
                  <div className="bg-muted px-4 py-1.5 text-xs font-mono text-muted-foreground uppercase tracking-wider border-b border-border">
                    {block.language}
                  </div>
                )}
                <pre className="bg-[hsl(var(--card))] p-4 overflow-x-auto">
                  <code className="text-sm font-mono text-foreground leading-relaxed whitespace-pre">
                    {block.code}
                  </code>
                </pre>
              </div>
            );

          case "table":
            return (
              <div key={i} className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted">
                      {block.headers.map((h, j) => (
                        <th key={j} className="px-4 py-2.5 text-left font-semibold text-foreground border-b border-border">
                          {renderInline(h)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, ri) => (
                      <tr key={ri} className={ri % 2 === 0 ? "bg-background" : "bg-muted/30"}>
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-4 py-2 text-muted-foreground border-b border-border/50">
                            {renderInline(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case "list":
            return (
              <ul key={i} className="space-y-1.5 ml-1">
                {block.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-primary mt-1.5 text-xs">●</span>
                    <span className="leading-relaxed">{renderInline(item)}</span>
                  </li>
                ))}
              </ul>
            );

          case "paragraph":
            return (
              <p key={i} className="text-muted-foreground leading-relaxed">
                {renderInline(block.text)}
              </p>
            );

          case "break":
            return <div key={i} className="h-1" />;

          default:
            return null;
        }
      })}
    </div>
  );
}
